// src/SecondaryOccupation.jsx
import React, { useState } from 'react';
import OccupationSelector from './OccupationSelector';
import { normalizeBranch } from './branch';
import './SecondaryOccupation.css';

/*
  LOGIC:
  - The primary OccupationSelector in Dashboard = Last Held Job (automatic, no input needed here)
  - This component handles PRIOR jobs only, going backwards in time
  - Each prior job: branch + code + year they cross-trained OUT of it
  - Display order: most recent prior job at top, oldest at bottom
*/

export default function SecondaryOccupation({ entries, onAdd, onRemove }) {
  const [phase, setPhase]        = useState('idle');   // idle | pickCode | pickYear
  const [pendingOcc, setPending] = useState(null);
  const [yearInput, setYearInput] = useState('');

  function startAdd() {
    setPending(null);
    setYearInput('');
    setPhase('pickCode');
  }

  function handlePick(occ) {
  // OccupationSelector fires onChange when branch changes too — ignore until code is selected
  if (!occ.code || occ.code.trim() === '') return;

  setPending({
    ...occ,
    branch: normalizeBranch(occ.branch),
  });
  setYearInput('');
  setPhase('pickYear');
}

  function commit(year) {
    if (!pendingOcc) return;
    onAdd({
      code:            pendingOcc.code,
      title:           pendingOcc.title,
      branch:          pendingOcc.branch,
      crossTrainedOut: year || null,
    });
    reset();
  }

  function reset() {
    setPending(null);
    setYearInput('');
    setPhase('idle');
  }

  // Sort prior jobs: most recent cross-train date first, nulls last
  const sorted = [...entries].sort((a, b) => {
    if (!a.crossTrainedOut && !b.crossTrainedOut) return 0;
    if (!a.crossTrainedOut) return 1;
    if (!b.crossTrainedOut) return -1;
    return Number(b.crossTrainedOut) - Number(a.crossTrainedOut);
  });

  return (
    <div className="soc-wrap">

      {/* ── Prior job entries ── */}
      {sorted.length > 0 && (
        <div className="soc-list">
          <div className="soc-list-label">Prior Military Jobs</div>
          {sorted.map((entry, i) => {
            const originalIndex = entries.indexOf(entry);
            return (
              <div key={`${entry.code}-${i}`} className="soc-row">
                <div className="soc-row-bar" />
                <div className="soc-row-body">
                  <div className="soc-row-top">
                    <div className="soc-row-identity">
                      <span className="soc-row-code">{entry.code}</span>
                      <span className="soc-row-dash">—</span>
                      <span className="soc-row-title">{entry.title}</span>
                    </div>
                    <button
                      className="soc-remove-btn"
                      onClick={() => onRemove(originalIndex)}
                      title="Remove"
                    >×</button>
                  </div>
                  <div className="soc-row-meta">
                    <span>{entry.branch}</span>
                    {entry.crossTrainedOut && (
                      <>
                        <span className="soc-meta-sep">·</span>
                        <span>Cross-trained out {entry.crossTrainedOut}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add button ── */}
      {entries.length < 7 && phase === 'idle' && (
        <button className="soc-add-btn" onClick={startAdd}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.8"
            strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Prior Military Job
        </button>
      )}

      {/* ── Phase: pick code ── */}
      {phase === 'pickCode' && (
        <div className="soc-panel">
          <div className="soc-panel-header">
            <span className="soc-panel-title">
              Select the AFSC / MOS / Rating you held <strong>before</strong> your last job
            </span>
            <button className="soc-cancel-btn" onClick={reset}>Cancel</button>
          </div>
          <OccupationSelector occupation={null} onChange={handlePick} />
        </div>
      )}

      {/* ── Phase: enter cross-train-out year ── */}
      {phase === 'pickYear' && pendingOcc && (
        <div className="soc-panel">
          <div className="soc-selected-code">
            <span className="soc-selected-code-val">{pendingOcc.code}</span>
            <span className="soc-selected-code-title">{pendingOcc.title}</span>
            <span className="soc-selected-branch">{pendingOcc.branch}</span>
          </div>
          <p className="soc-year-q">
            What year did you <strong>cross-train out</strong> of this role?
            <span className="soc-optional"> (Optional)</span>
          </p>
          <p className="soc-year-hint">
            Enter the year you <em>left</em> this AFSC — not when you entered your next one.
          </p>
          <div className="soc-year-row">
            <input
              type="number"
              className="soc-year-input"
              placeholder="e.g. 2010"
              min="1970"
              max={new Date().getFullYear()}
              value={yearInput}
              onChange={e => setYearInput(e.target.value)}
            />
            <button className="soc-confirm-btn" onClick={() => commit(yearInput.trim())}>
              Save
            </button>
            <button className="soc-skip-btn" onClick={() => commit('')}>
              Skip Year
            </button>
            <button className="soc-cancel-link" onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

    </div>
  );
}